import { Injectable } from '@angular/core';
import { EAnalyticsStatus, EMatchingType } from '@enums/campaign-analytics.enum';
import {
  EDetailLevel,
  ICampaignExportApi,
} from '../campaign-details-banner/campaign-export-dialog/campaign-export-dialog.interface';
import { ICampaignExportConfig } from './campaign-export-config.interface';

interface IExportContext {
  noContentType: boolean;
  matchedSelected: boolean;
  hasUsefulContentType: boolean;
}

@Injectable({ providedIn: 'root' })
export class CampaignExportConfigService {
  public calculateExportConfig(exportMode: boolean, campaignExport: ICampaignExportApi | null): ICampaignExportConfig {
    const ctx = this.buildContext(exportMode, campaignExport);

    return {
      ...this.buildClickConfig(exportMode, campaignExport),
      ...this.buildAllowedStatusesConfig(ctx, exportMode, campaignExport),
      ...this.buildStatusFilterConfig(ctx, exportMode, campaignExport),
      ...this.buildOverviewConfig(ctx, exportMode, campaignExport),
    };
  }

  private buildContext(exportMode: boolean, campaignExport: ICampaignExportApi | null): IExportContext {
    const noContentType = exportMode && !!campaignExport && !campaignExport.showContentMatchingType;
    const matchedSelected =
      !exportMode ||
      !campaignExport ||
      (campaignExport.showContentMatchingType &&
        (!campaignExport.contentMatchingTypes?.length ||
          campaignExport.contentMatchingTypes.some((cm) => cm.matchingType === EMatchingType.matched)));
    const hasUsefulContentType =
      !exportMode ||
      !campaignExport ||
      (!noContentType &&
        (!campaignExport.contentMatchingTypes?.length ||
          campaignExport.contentMatchingTypes.some((cm) => cm.matchingType !== EMatchingType.unrelated)));

    return { hasUsefulContentType, matchedSelected, noContentType };
  }

  private buildClickConfig(
    exportMode: boolean,
    campaignExport: ICampaignExportApi | null,
  ): Pick<
    ICampaignExportConfig,
    'allowDeliverableGroupClick' | 'allowDeliverablesRowClick' | 'allowKpiGoalsRowClick' | 'allowObservationsRowClick'
  > {
    const notGeneralInfo = !!campaignExport && campaignExport.detailLevel !== EDetailLevel.generalInfo;
    const detailedAnalytics = campaignExport?.detailLevel === EDetailLevel.detailedAnalytics;

    return {
      allowDeliverableGroupClick: !exportMode || notGeneralInfo,
      allowDeliverablesRowClick: !exportMode || detailedAnalytics,
      allowKpiGoalsRowClick: !exportMode || notGeneralInfo,
      allowObservationsRowClick: !exportMode || detailedAnalytics,
    };
  }

  private buildAllowedStatusesConfig(
    ctx: IExportContext,
    exportMode: boolean,
    campaignExport: ICampaignExportApi | null,
  ): Pick<
    ICampaignExportConfig,
    | 'allowedContentMatchingTypes'
    | 'allowedContentStatuses'
    | 'allowedDeliverableStatuses'
    | 'allowedKpiStatuses'
    | 'allowedObservationStatuses'
  > {
    const { noContentType, matchedSelected } = ctx;

    return {
      allowedContentMatchingTypes: this.getAllowedContentMatchingTypes(exportMode, campaignExport),
      allowedContentStatuses: this.getAllowedStatuses(
        exportMode,
        campaignExport,
        noContentType,
        campaignExport?.showContentStatus ?? true,
        campaignExport?.contentStatuses?.map((cs) => cs.status) ?? null,
      ),
      allowedDeliverableStatuses: this.getAllowedStatuses(
        exportMode,
        campaignExport,
        noContentType,
        campaignExport?.showDeliverableStatus ?? true,
        campaignExport?.deliverableStatuses?.map((ds) => ds.status) ?? null,
      ),
      allowedKpiStatuses: this.getAllowedStatuses(
        exportMode,
        campaignExport,
        noContentType || !matchedSelected,
        campaignExport?.showKpiStatus ?? true,
        campaignExport?.kpiStatuses?.map((ks) => ks.status) ?? null,
      ),
      allowedObservationStatuses: this.getAllowedStatuses(
        exportMode,
        campaignExport,
        noContentType,
        campaignExport?.showObservationStatus ?? true,
        campaignExport?.observationStatuses?.map((os) => os.status) ?? null,
      ),
    };
  }

  private buildStatusFilterConfig(
    ctx: IExportContext,
    exportMode: boolean,
    campaignExport: ICampaignExportApi | null,
  ): Pick<
    ICampaignExportConfig,
    | 'showContentsStatusFilter'
    | 'showDeliverablesStatusFilter'
    | 'showKpiGoalsStatusFilter'
    | 'showObservationsStatusFilter'
  > {
    const { noContentType, matchedSelected } = ctx;
    const notDisabled = !exportMode || !noContentType;

    return {
      showContentsStatusFilter: notDisabled && matchedSelected && campaignExport?.showContentStatus !== false,
      showDeliverablesStatusFilter: notDisabled && campaignExport?.showDeliverableStatus !== false,
      showKpiGoalsStatusFilter: notDisabled && matchedSelected && campaignExport?.showKpiStatus !== false,
      showObservationsStatusFilter: notDisabled && campaignExport?.showObservationStatus !== false,
    };
  }

  private buildOverviewConfig(
    ctx: IExportContext,
    exportMode: boolean,
    campaignExport: ICampaignExportApi | null,
  ): Pick<
    ICampaignExportConfig,
    | 'showOverviewContentExecution'
    | 'showOverviewContentStatus'
    | 'showOverviewDeliverablesCard'
    | 'showOverviewKpiCard'
  > {
    const { hasUsefulContentType, matchedSelected } = ctx;

    return {
      showOverviewContentExecution: hasUsefulContentType,
      showOverviewContentStatus: !exportMode || !campaignExport || matchedSelected,
      showOverviewDeliverablesCard: !exportMode || (matchedSelected && campaignExport?.showDeliverableStatus !== false),
      showOverviewKpiCard: !exportMode || (matchedSelected && campaignExport?.showKpiStatus !== false),
    };
  }

  /**
   * Returns null (all allowed), [] (none), or specific matching types.
   */
  private getAllowedContentMatchingTypes(
    exportMode: boolean,
    campaignExport: ICampaignExportApi | null,
  ): EMatchingType[] | null {
    if (!exportMode || !campaignExport) {
      return null;
    }

    if (!campaignExport.showContentMatchingType) {
      return [];
    }

    if (!campaignExport.contentMatchingTypes?.length) {
      return null;
    }

    return campaignExport.contentMatchingTypes.map((cm) => cm.matchingType);
  }

  /**
   * Returns null (all allowed), [] (none/disabled), or the specific EAnalyticsStatus subset.
   *
   * @param disabledByContext - true when the section is force-disabled (e.g. no content type selected, Matched missing)
   * @param showFlag - the export's show flag for this status group
   * @param selectedStatuses - the API-level statuses stored in the export (null/empty = all)
   */
  private getAllowedStatuses(
    exportMode: boolean,
    campaignExport: ICampaignExportApi | null,
    disabledByContext: boolean,
    showFlag: boolean,
    selectedStatuses: EAnalyticsStatus[] | null,
  ): EAnalyticsStatus[] | null {
    if (!exportMode || !campaignExport) {
      return null;
    }

    if (disabledByContext || !showFlag) {
      return [];
    }

    if (!selectedStatuses?.length) {
      return null;
    }

    return selectedStatuses;
  }
}
